
import { supabase } from '@/integrations/supabase/client';

export interface AdminStats {
  clientCount: number;
  practitionerCount: number;
  pendingPractitionerCount: number;
  bookingCount: number;
  recentClients: any[];
  recentPractitioners: any[];
  recentBookings: any[];
}

/**
 * Fetches admin dashboard statistics
 * @returns Object containing dashboard statistics
 */
export const getAdminStats = async (): Promise<AdminStats> => {
  // Get counts
  const { count: clientCount } = await supabase
    .from('clients')
    .select('*', { count: 'exact', head: true });

  const { count: practitionerCount } = await supabase
    .from('practitioners')
    .select('*', { count: 'exact', head: true });

  const { count: pendingPractitionerCount } = await supabase
    .from('practitioners')
    .select('*', { count: 'exact', head: true })
    .eq('profile_status', 'pending_review');

  const { count: bookingCount } = await supabase
    .from('bookings')
    .select('*', { count: 'exact', head: true });

  // Get recent clients
  const { data: recentClients } = await supabase
    .from('clients')
    .select('id, first_name, last_name, created_at')
    .order('created_at', { ascending: false })
    .limit(5);

  // Get recent practitioners
  const { data: recentPractitioners } = await supabase
    .from('practitioners')
    .select('id, first_name, last_name, profile_status, created_at')
    .order('created_at', { ascending: false })
    .limit(5);

  // Get recent bookings
  const { data: recentBookings } = await supabase
    .from('bookings')
    .select(`
      id, 
      start_time, 
      status, 
      price_at_booking,
      currency_at_booking,
      clients:client_id (first_name, last_name),
      practitioners:practitioner_id (first_name, last_name)
    `)
    .order('created_at', { ascending: false })
    .limit(5);

  return {
    clientCount: clientCount || 0,
    practitionerCount: practitionerCount || 0,
    pendingPractitionerCount: pendingPractitionerCount || 0,
    bookingCount: bookingCount || 0,
    recentClients: recentClients || [],
    recentPractitioners: recentPractitioners || [],
    recentBookings: recentBookings || []
  };
};

/**
 * Checks if a user is an admin
 * @param userId The user ID to check
 * @returns Boolean indicating if the user is an admin
 */
export const checkIsAdmin = async (userId: string): Promise<boolean> => {
  try {
    console.log('Checking admin status for user ID:', userId);
    
    const { data, error } = await supabase
      .from('admins')
      .select('user_id')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
    
    console.log('Admin check result:', data);
    return !!data;
  } catch (error) {
    console.error('Error in checkIsAdmin:', error);
    return false;
  }
};
